import { instance, headers, unwrapList, get, cmsParams, fetchPaged } from "./shared";
import {
  fetchDeliveryNotesByPlate,
  mapVehicleDeliveryNote,
  summarizeDeliveryNotes,
} from "./deliveryNotes";

const onlineDeviceIds = (ctx = {}) => {
  const ids = new Set();
  (ctx.allNotifications?.statusOnline || []).forEach((item) => {
    if (item?.id != null) ids.add(Number(item.id));
  });
  (ctx.devices3 || []).forEach((device) => {
    if (
      device?.status === "online" ||
      device?.statusClass === "online" ||
      device?.statusText === "online"
    ) {
      ids.add(Number(device.id));
    }
  });
  const related = ctx.deviceRelatedData || {};
  Object.keys(related).forEach((id) => {
    const row = related[id];
    if (row?.statusClass === "online" || row?.status === "online") {
      ids.add(Number(id));
    }
  });
  return ids;
};

const odometerKm = (vehicle, ctx) => {
  const stored = Number(vehicle?.odometer);
  if (!Number.isNaN(stored) && stored > 0) return stored;
  const deviceId = vehicle?.deviceId;
  if (!deviceId) return 0;
  const position = (ctx.positions || []).find(
    (item) => Number(item.deviceId) === Number(deviceId)
  );
  const meters = Number(position?.attributes?.totalDistance);
  if (!Number.isNaN(meters) && meters > 0) return meters / 1000;
  return 0;
};

export const mapVehicle = (vehicle, ctx = {}) => {
  const make = vehicle?.attributes?.make || vehicle?.makeName || "";
  const model = vehicle?.attributes?.model || vehicle?.modelName || "";
  const plate =
    vehicle?.vehicleLicensePlate ||
    vehicle?.vehicleLP ||
    vehicle?.plateNumber ||
    "";
  const name =
    vehicle?.label ||
    [make, model].filter(Boolean).join(" ") ||
    plate ||
    `Vehicle ${vehicle?.id}`;
  const online = onlineDeviceIds(ctx).has(Number(vehicle?.deviceId));

  return {
    id: vehicle?.id,
    name,
    plateNumber: plate,
    make,
    model,
    status: online ? "active" : "inactive",
    odometer: odometerKm(vehicle, ctx),
    deviceId: vehicle?.deviceId,
    lastServiceDate: null,
    lastServiceName: "",
    totalMaintenanceCost: 0,
    nextPredictedService: "",
    nextPredictedDate: null,
    nextPredictedCost: 0,
    raw: vehicle,
  };
};

export async function fetchVehiclesLive(ctx) {
  const userId = ctx?.logInUser?.id;

  // CMS admin browse: /api/vehicles/list (same as VehicleSelector)
  try {
    const listRows = await fetchPaged(
      (page, pageSize) =>
        `/api/vehicles/list?page=${page}&limit=${pageSize}`,
      { limit: 100, maxPages: 5 }
    );
    if (listRows.length) {
      return listRows.map((item) => mapVehicle(item, ctx));
    }
  } catch (err) {
    // fall through to account-scoped get
  }

  if (userId) {
    try {
      const res = await get("/api/vehicles/get", {
        userId,
        all: true,
        limit: -1,
      });
      const list = unwrapList(res);
      if (list.length) return list.map((item) => mapVehicle(item, ctx));
    } catch (err) {
      if (!ctx?.vehicles?.length) throw err;
    }
  }
  if (ctx?.vehicles?.length) {
    return ctx.vehicles.map((item) => mapVehicle(item, ctx));
  }
  return [];
}

export function fetchVehicleById(selected, ctx) {
  if (selected && typeof selected === "object") {
    if (selected.plateNumber != null || selected.name) {
      return selected;
    }
    return mapVehicle(selected, ctx);
  }

  const lists = [ctx?.vehicleOptions, ctx?.vehicles];
  for (const list of lists) {
    const found = (list || []).find(
      (item) => String(item.id) === String(selected)
    );
    if (found) {
      if (found.plateNumber != null || found.name) return found;
      return mapVehicle(found, ctx);
    }
  }

  const error = new Error("Vehicle not found");
  error.status = 404;
  throw error;
}

const looksLikeFleetSize = (payload) =>
  payload &&
  typeof payload === "object" &&
  !Array.isArray(payload) &&
  (payload.totalFleetCount != null ||
    Array.isArray(payload.data) ||
    Object.values(payload).some((value) => typeof value === "number"));

const normalizeFleetSize = (payload) => {
  // CMS Maintenance Dashboard source: GET /api/dashboards/cms/2
  if (
    payload?.totalFleetCount != null ||
    (Array.isArray(payload?.data) && payload.data.length)
  ) {
    const categories = (payload.data || [])
      .map((item) => ({
        name:
          item.contractNumber ||
          item.accountName ||
          item.clientName ||
          item.name ||
          item.label ||
          "—",
        value: Number(item.accessVehiclesCount ?? item.count ?? item.value) || 0,
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
    const total =
      Number(payload.totalFleetCount) ||
      categories.reduce((sum, item) => sum + item.value, 0);
    return { total, categories };
  }

  const entries = Object.entries(payload || {}).filter(
    ([, value]) => typeof value === "number"
  );
  const total = entries.reduce((sum, [, value]) => sum + (Number(value) || 0), 0);
  const categories = entries
    .map(([name, value]) => ({ name, value: Number(value) || 0 }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);
  return { total, categories };
};

/** Same source as Maintenance Dashboard fleet size: GET /api/dashboards/cms/2 */
export async function fetchFleetSizeLive(ctx = {}, params = {}) {
  const base = cmsParams(ctx);
  const query = Object.entries({ ...base, ...params })
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
  const url = `/api/dashboards/cms/2?${query}`;

  try {
    const res = await instance({ url, method: "GET", headers });
    if (looksLikeFleetSize(res)) return normalizeFleetSize(res);
    if (looksLikeFleetSize(res?.data)) return normalizeFleetSize(res.data);
    return { total: 0, categories: [] };
  } catch (err) {
    if (looksLikeFleetSize(err)) return normalizeFleetSize(err);
    if (looksLikeFleetSize(err?.data)) return normalizeFleetSize(err.data);
    throw err;
  }
}

const unwrapThroughput = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  if (
    payload.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data) &&
    (payload.data.totalServedVehicles != null ||
      payload.data.totalVehicles != null)
  ) {
    return payload.data;
  }
  return payload;
};

const normalizeThroughput = (payload) => {
  const src = unwrapThroughput(payload) || {};
  return {
    totalServedVehicles: Number(src.totalServedVehicles) || 0,
    totalDeliveryNotesCount: Number(src.totalDeliveryNotesCount) || 0,
    totalVehicles: Number(src.totalVehicles) || 0,
  };
};

const looksLikeThroughput = (payload) => {
  const src = unwrapThroughput(payload);
  return (
    src &&
    typeof src === "object" &&
    (src.totalServedVehicles != null ||
      src.totalDeliveryNotesCount != null ||
      src.totalVehicles != null)
  );
};

/** Same source as Sales KPI: GET /api/nskpi/fleet_throughput/?contractType=1 */
export async function fetchFleetThroughputLive(ctx = {}) {
  const contractType = cmsParams(ctx).contractType;
  const url = `/api/nskpi/fleet_throughput/?contractType=${contractType}`;

  try {
    const res = await instance({ url, method: "GET", headers });
    return normalizeThroughput(res);
  } catch (err) {
    if (looksLikeThroughput(err)) return normalizeThroughput(err);
    if (looksLikeThroughput(err?.data)) return normalizeThroughput(err.data);
    throw err;
  }
}

export async function loadVehiclesQuestion(questionId, vehicleId, ctx) {
  switch (questionId) {
    case "totalVehicles": {
      const [vehicles, fleetSize] = await Promise.all([
        fetchVehiclesLive(ctx),
        fetchFleetSizeLive(ctx).catch(() => ({ total: 0, categories: [] })),
      ]);
      return { vehicles, fleetSize };
    }

    case "servedVehicles": {
      const throughput = await fetchFleetThroughputLive(ctx);
      return { throughput };
    }

    case "mileageSummary":
    case "vehiclePicker": {
      const vehicles = await fetchVehiclesLive(ctx);
      return { vehicles };
    }

    case "vehicleDetails": {
      const vehicle = fetchVehicleById(vehicleId, ctx);
      const notes = await fetchDeliveryNotesByPlate(vehicle.plateNumber).catch(
        () => []
      );
      const mappedNotes = notes.map(mapVehicleDeliveryNote);
      return {
        vehicle,
        deliveryNotes: mappedNotes,
        deliveryNoteSummary: summarizeDeliveryNotes(notes),
      };
    }

    default:
      return null;
  }
}
